import type { AuthTranslations } from './types'

/**
 * English translations for Auth domain
 */
export const enTranslations: AuthTranslations = {
  // Page/Section level
  title: "Authentication",
  subtitle: "Manage authentication settings",

  // Actions
  addAuth: "Add Authentication",
  editAuth: "Edit Authentication",
  deleteAuth: "Delete Authentication",

  // Search and filters
  searchPlaceholder: "Search authentication...",
  noItemsFound: "No authentication items found",
  noItemsDescription: "You haven't added any authentication items yet. Add your first item to get started.",
  addFirst: "Add Your First Authentication",

  // Form section (basic)
  form: {
    name: "Name",
    namePlaceholder: "Enter name",
    status: "Status",
    validation: {
      nameRequired: "Name is required",
    }
  },

  // Status
  status: {
    active: "Active",
    inactive: "Inactive",
  },

  // Messages
  messages: {
    createSuccess: "Authentication created successfully",
    updateSuccess: "Authentication updated successfully",
    deleteSuccess: "Authentication deleted successfully",
    deleteConfirm: "Are you sure you want to delete this authentication?",
  },

  // Table headers
  table: {
    name: "Name",
    status: "Status",
  },

  // Login page
  signIn: "Sign In",
  signUp: "Sign Up",
  welcomeBack: "Welcome Back",
  loginSubtitle: "Sign in to your United Feed Company Customer Portal",
  enterCredentials: "Enter your credentials to access your account",
  rememberMe: "Remember me",
  forgotPassword: "Forgot password?",
  forgotPasswordDescription: "Enter your email address and we'll send you a password reset link",
  noAccount: "Don't have an account?",
  alreadyHaveAccount: "Already have an account?",

  // Registration
  createAccount: "Create Account",
  registerSubtitle: "Join the United Feed Company Customer Portal today",
  registrationSuccessful: "Registration Successful",
  registrationSuccessMessage: "Your account has been created successfully",
  registrationPendingMessage: "Thanks for registering. Your account is pending activation and you'll be able to sign in once it's approved.",
  fillDetails: "Fill in your details to create an account",
  fullName: "Full Name",
  fullNamePlaceholder: "Enter your full name",
  companyName: "Company Name",
  companyNamePlaceholder: "Enter your company name",
  phone: "Phone Number",
  phonePlaceholder: "+966 5XX XXX XXXX",
  accountType: "Account Type",
  selectAccountType: "Select account type",
  accountContractor: "Contractor",
  accountSupplier: "Supplier",
  accountDistributor: "Distributor",
  accountRetail: "Retail Customer",

  // Terms
  termsNotice: "By continuing, you agree to our",
  termsOfService: "Terms of Service",
  privacyPolicy: "Privacy Policy",
  and: "and",
  agreeToTerms: "I agree to the",

  // Password reset
  resetPassword: "Reset Password",
  resetSubtitle: "We'll help you get back into your account",
  resetInstructions: "Enter your email address and we'll send you a link to reset your password",
  sendResetLink: "Send Reset Link",
  backToLogin: "Back to login",
  checkEmail: "Check Your Email",
  resetEmailSent: "Reset Email Sent",
  resetEmailSentMessage: "We've sent a password reset link to {{email}}",
  didntReceiveEmail: "Didn't receive the email? Check your spam folder or try again.",
  tryAgain: "Try Again",
  passwordResetSuccessful: "Password Reset Successful",
  passwordResetSuccessMessage: "Your password has been reset successfully",
  resetPasswordDescription: "Enter your new password below",
  newPassword: "New Password",
  currentPassword: "Current Password",

  // Toasts / notifications
  loginSuccess: "Welcome back!",
  loginSuccessMessage: "You are now signed in.",
  loginError: "Login failed",
  registerSuccess: "Registration successful",
  registerError: "Registration failed",
  logoutSuccess: "Logged out",
  logoutSuccessMessage: "You have been logged out.",
  passwordResetSent: "Reset link sent",
  passwordResetError: "Password reset failed",
  passwordResetSuccess: "Password updated",
  changePasswordSuccess: "Password updated",
  changePasswordSuccessMessage: "Your password has been changed successfully.",
  changePasswordError: "Password change failed",
  changePasswordOtpSent: "Verification code sent",
  changePasswordOtpError: "Could not send verification code",

  // Form fields
  email: "Email",
  emailPlaceholder: "Enter your email",
  emailOrPhone: "Email or Phone Number",
  emailOrPhonePlaceholder: "Enter your email or phone number",
  password: "Password",
  passwordPlaceholder: "Enter your password",
  confirmPassword: "Confirm Password",
  confirmPasswordPlaceholder: "Confirm your password",
  passwordsDoNotMatch: "Passwords do not match",

  // Buttons
  buttons: {
    signIn: "Sign In",
    signUp: "Sign Up",
    forgotPassword: "Forgot Password",
    resetPassword: "Reset Password",
    backToLogin: "Back to Login",
  },

  // Standalone convenience keys (duplicates of nested keys for easier access)
  name: "Name",
  namePlaceholder: "Enter name",
  statusLabel: "Status",
  nameRequired: "Name is required",
  active: "Active",
  inactive: "Inactive",
  createSuccess: "Authentication created successfully",
  updateSuccess: "Authentication updated successfully",
  deleteSuccess: "Authentication deleted successfully",
  deleteConfirm: "Are you sure you want to delete this authentication?",

  // Registration — new fields
  mobilePhone: 'Mobile Phone',
  mobilePhonePlaceholder: '+966 5XX XXX XXXX',
  telephone: 'Telephone',
  telephonePlaceholder: '+966 1XX XXX XXXX',
  vatNumber: 'VAT Number',
  vatNumberPlaceholder: '3XXXXXXXXXX3',
  addressLine1: 'Street Address',
  addressLine1Placeholder: 'Street name',
  houseNumber: 'Building No.',
  city: 'City',
  cityPlaceholder: 'e.g. Riyadh',
  region: 'Region',
  postalCode: 'Postal Code',
  country: 'Country',
  selectCountry: 'Select country',
  selectRegion: 'Select region',
  vatSection: 'VAT Information',
  crSection: 'Commercial Registration',
  crNumber: 'CR Number',
  addressSection: 'Address',
  vatCertificate: 'VAT Certificate',
  vatCertificateHint: 'Upload your official VAT certificate (PDF or image)',
  crDocument: 'Commercial Registration (CR)',
  crDocumentHint: 'Upload your Commercial Registration certificate (PDF or image)',
  selectCity: 'Select city',
  chooseFile: 'Choose file',
  attachments: 'Documents',
  attachmentsHint: 'Upload your VAT certificate and Commercial Registration (CR)',
  addDocument: 'Add Document',
  businessSegment: 'Business Segment',
  customerSegment: 'Customer Segment',
  customerSegmentHint: 'Select the category that best describes your business type.',
  selectSegment: 'Select segment',
  segmentTransport: 'Transport',
  segmentPlants: 'Plants',
  segmentBagCustomers: 'Bag Customers',
  regionRiyadh: 'Riyadh',
  regionMakkah: 'Makkah',
  regionMadinah: 'Madinah',
  regionQassim: 'Qassim',
  regionEastern: 'Eastern Province',
  regionAsir: 'Asir',
  regionTabuk: 'Tabuk',
  regionHail: 'Hail',
  regionNorthernBorders: 'Northern Borders',
  regionJazan: 'Jazan',
  regionNajran: 'Najran',
  regionBahah: 'Al-Bahah',
  regionJawf: 'Al-Jawf',
  accountInfo: 'Account Info',
  companyDetails: 'Company Details',
  companyDetailsSubtitle: 'Fill in your company and address details',
  nextStep: 'Next',
  back: 'Back',

  // API error keys returned by the backend as i18n keys
  errors: {
    invalidCredentials: "Invalid email/phone or password",
    accountLocked: "Your account has been locked due to too many failed login attempts. Reset your password or contact an administrator.",
    emailNotConfirmed: "Your account email is not confirmed",
    accountBlocked: "Your account has been blocked by an administrator",
    accountInactive: "Your account has been set to inactive by an administrator",
    noTenant: "Your account is not associated with a tenant yet",
    accountPending: "Your account is not active yet. Please wait for activation.",
    accountSuspended: "Your account has been suspended by an administrator",
    registerDisabled: "Registration is currently disabled",
    emailAlreadyTaken: "Email or username are already taken",
    phoneTaken: "Phone number is already registered",
    sapBusinessPartnerNumberTaken: "SAP Business Partner number is already registered",
    phoneInvalid: "Please enter a valid Saudi mobile number",
    vatCertificateRequired: "VAT certificate file is required",
    crDocumentRequired: "Commercial Registration document is required",
    loginFailed: "Login failed. Please try again.",
    registrationFailed: "Registration failed. Please try again.",
    deleteAccountFailed: "Account deletion failed. Please try again.",
    updateProfileFailed: "Profile update failed. Please try again.",
    passwordChangeFailed: "Password change failed. Please try again.",
    verificationCodeFailed: "Verification code request failed. Please try again.",
    passwordResetRequestFailed: "Password reset request failed. Please try again.",
    passwordResetFailed: "Password reset failed. Please try again.",
  },

  // API success keys returned by the backend as i18n keys
  success: {
    registrationPending: "Registration received. Your account will be activated before you can sign in.",
  },

  // Account action results
  deleteAccountSuccess: "Account deleted",
  deleteAccountFailed: "Account deletion failed",
  updateProfileSuccess: "Profile updated",
  updateProfileSuccessDesc: "Your changes have been saved.",
  updateProfileFailed: "Profile update failed",

  // Lockout
  accountLocked: "Account locked. Reset your password or contact an administrator.",
  attemptsRemaining: "Last attempt before your account is locked.",

  // Session timeout
  sessionExpired: "Session Expired",
  sessionExpiredMessage: "You have been logged out due to inactivity.",

  // Validation
  validation: {
    fullNameRequired: "Full name is required",
    companyNameRequired: "Company name is required",
    emailRequired: "Valid email is required",
    passwordMin: "Password must be at least 6 characters",
    passwordMismatch: "Passwords do not match",
    agreeToTermsRequired: "You must agree to the terms",
    currentPasswordRequired: "Current password is required",
    passwordRequired: "Password is required",
    passwordMinLength: "Password must be at least 6 characters",
    passwordMaxLength: "Password must be 100 characters or less",
    confirmPasswordRequired: "Confirm password is required",
    passwordsMismatch: "Passwords do not match",
    countryRequired: 'Country is required',
    customerSegmentRequired: 'Customer segment is required',
    vatNumberRequired: 'VAT number is required',
    vatCertificateRequired: 'VAT certificate file is required',
    crNumberRequired: 'CR number is required',
    crDocumentRequired: 'Commercial Registration document is required',
    addressLine1Required: 'Address is required',
    houseNumberInvalid: 'Building number must be up to 4 digits',
    cityRequired: 'City is required',
    regionRequired: 'Region is required',
    postalCodeRequired: 'Postal code is required',
    postalCodeLength: 'Postal code must be exactly 5 characters',
  },
}
